import React from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface ConnectionData {
  id: number
  name: string
  status: 'pending' | 'approved' | 'denied' | 'revoked'
  metadata: Record<string, string>
  last_used_at: string | null
  approved_at: string | null
  approved_by: string | null
  revoked_at: string | null
  revoked_by: string | null
  created_at: string
  agent_username: string
  agent_slug: string
}

interface Props {
  connections: ConnectionData[]
}

const statusColors: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  denied: 'error',
  revoked: 'default',
}

export default function ConnectionRequests({ connections }: Props) {
  function handleApprove(id: number) {
    router.post(`/manage/agents/connection_requests/${id}/approve`)
  }

  function handleDeny(id: number) {
    router.post(`/manage/agents/connection_requests/${id}/deny`)
  }

  function handleRevoke(id: number) {
    if (confirm('Are you sure you want to revoke this connection? The agent will lose access immediately.')) {
      router.post(`/manage/agents/connection_requests/${id}/revoke`)
    }
  }

  return (
    <SidebarLayout section="manage" title="Connections">
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Connection</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map((conn) => (
              <TableRow key={conn.id}>
                <TableCell>
                  <Link href={`/manage/agents/${conn.agent_slug}/edit`} style={{ color: '#1a1a1a', textDecoration: 'none' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {conn.agent_username}
                    </Typography>
                  </Link>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {conn.name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={conn.status}
                    size="small"
                    color={statusColors[conn.status] || 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {conn.last_used_at ? new Date(conn.last_used_at).toLocaleString() : '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(conn.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {conn.status === 'pending' && (
                    <>
                      <Button size="small" color="success" onClick={() => handleApprove(conn.id)} sx={{ mr: 1 }}>
                        Approve
                      </Button>
                      <Button size="small" color="error" onClick={() => handleDeny(conn.id)}>
                        Deny
                      </Button>
                    </>
                  )}
                  {conn.status === 'approved' && (
                    <Button size="small" color="error" onClick={() => handleRevoke(conn.id)}>
                      Revoke
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {connections.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No connections found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SidebarLayout>
  )
}
